<template>
  <div id="app" class="h-full">
    <router-view />
    <portal-target name="modals" multiple></portal-target>

    <div
      aria-live="assertive"
      class="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 z-20"
    >
      <transition-group
        class="w-full flex flex-col items-center space-y-4 sm:items-end"
        enter-active-class="transform ease-out duration-300 transition"
        leave-active-class="transition ease-in duration-100"
        enter-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          class="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
          v-bind:key="'msg'"
          v-if="showUpdateAvailableToast"
        >
          <div class="p-4">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <!-- Heroicon name: outline/inbox -->
                <div
                  class="h-6 w-6 text-gray-400"
                  v-on:click="showUpdateAvailableToast = false"
                >
                  <i class="fa-solid fa-rotate-exclamation"></i>
                </div>
              </div>
              <div class="ml-3 w-0 flex-1 pt-0.5">
                <p class="text-sm font-medium text-gray-900">
                  Update Available
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  A new version of the web app is available.
                </p>
                <div class="mt-3 flex space-x-7">
                  <button
                    type="button"
                    class="bg-white rounded-md text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
                    v-on:click="updateApp"
                  >
                    Refresh Now
                  </button>
                  <button
                    type="button"
                    class="bg-white rounded-md text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none"
                    v-on:click="showUpdateAvailableToast = false"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <div class="ml-4 flex-shrink-0 flex">
                <button
                  type="button"
                  class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                  v-on:click="showUpdateAvailableToast = false"
                >
                  <span class="sr-only">Close</span>
                  <!-- Heroicon name: solid/x -->
                  <svg
                    class="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <ToastCard
          v-for="message in toastMessages"
          v-bind:key="message.id"
          :message="message"
        />
      </transition-group>
    </div>
  </div>
</template>

<script>
import * as Config from "@/config";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import ToastCard from "@/components/ToastCard";

export default {
  name: "app",
  data() {
    return {
      lastActiveTime: null,
      showUpdateAvailableToast: false,
      hasAskedUserToUpdate: false
    };
  },
  components: { ToastCard },
  mounted() {
    document.onload = this.resetTimer;
    document.onmousemove = this.resetTimer;
    document.onmousedown = this.resetTimer;
    document.ontouchstart = this.resetTimer;
    document.onclick = this.resetTimer;

    setTimeout(
      function() {
        this.ping();
      }.bind(this),
      1000
    );

    setInterval(
      function() {
        this.ping();
      }.bind(this),
      60000
    );
  },
  computed: {
    ...mapGetters(["toastMessages"])
  },
  methods: {
    ...mapGetters(["isAuthenticated", "getAuthorizationHeader"]),
    ...mapActions(["addToastMessage"]),

    resetTimer() {
      this.lastActiveTime = moment
        .utc(new Date())
        .format("YYYY-MM-DD HH:mm:ss");
    },

    async ping() {
      if (!this.isAuthenticated()) return;
      if (!this.lastActiveTime) {
        this.resetTimer();
      }
      let res = await this.$http.post(
        Config.apiPath + "/api/v2/ping?env=" + process.env.VUE_APP_VERCEL_ENV,
        {
          lastActive: this.lastActiveTime,
          version: process.env.VUE_APP_GIT_HASH
        }
      );

      if (
        process.env.VUE_APP_GIT_HASH !== res.data.version &&
        process.env.NODE_ENV !== "development"
      ) {
        this.showUpdateAvailableToast = true;
        if (!this.hasAskedUserToUpdate) {
          this.hasAskedUserToUpdate = true;
        }
      }
    },

    updateApp() {
      window.location.reload();
    }
  }
};
</script>
